import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { Button, Popconfirm, Table, Tag, Tooltip, Typography } from 'antd';
import { useStore } from 'effector-react';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { SortOrder } from 'antd/lib/table/interface';
import moment from 'moment';

import registrationIps, {
  getList,
  banRegistrationIp,
  unbanRegistrationIp,
} from '@stores/registrationIps';
import IRegistrationIp from '@interfaces/IRegistrationIp';
import useTranslate from '@hooks/useTranslate';
import componentContent from './RegistrationIpsTable.content.json';
import { pageSizeOptions } from '@constants/pagination';
import { DATE_FORMAT } from '@config/format';
import useGuards from '@hooks/useGuards';
import routes from '@config/routes';
import { getPath } from '@helpers/navigation';

interface RegistrationIpsTableProps {}

const RegistrationIpsTable: FunctionComponent<RegistrationIpsTableProps> = () => {
  const { list, preloaders, pagination, filters } = useStore(registrationIps);
  const content = useTranslate(componentContent);
  const { isAdmin } = useGuards();

  const [currentSortedColumnKey, setCurrentSortedColumnKey] = useState(
    (filters.sort || '').split(',')[0],
  );
  const [currentSortDirection, setCurrentSortDirection] = useState(
    (filters.sort || '').split(',')[1],
  );

  useEffect(() => {
    const [sortedColumnKey, sortDirection] = (filters.sort || '').split(',');
    setCurrentSortedColumnKey(sortedColumnKey);
    setCurrentSortDirection(sortDirection);
  }, [filters]);

  const onChange = useCallback((pagination: TablePaginationConfig, _: any, sorter: any) => {
    localStorage.setItem('registrationIpsPageSize', `${pagination.pageSize}`);

    getList({
      pagination,
      sorter,
    });
  }, []);

  const getStatusLabel = (status?: string) => {
    if (!status || status === 'FREE') return <Tag color='success'>{content.status_FREE}</Tag>;

    if (status === 'BAN') {
      return <Tag color='error'>{content.status_BAN}</Tag>;
    }

    if (status === 'UNBAN_MANUAL') {
      return (
        <Tag
          color='warning'
          style={{
            whiteSpace: 'normal',
            wordBreak: 'keep-all',
            overflowWrap: 'normal',
            lineHeight: '16px',
          }}
        >
          {content.status_UNBAN_MANUAL}
        </Tag>
      );
    }

    if (status === 'UNBAN_AUTO') {
      return (
        <Tag
          color='warning'
          style={{
            whiteSpace: 'normal',
            wordBreak: 'keep-all',
            overflowWrap: 'normal',
            lineHeight: '16px',
          }}
        >
          {content.status_UNBAN_AUTO}
        </Tag>
      );
    }

    return <Tag>{status}</Tag>;
  };

  const getAffectedAccountsCount = (item: IRegistrationIp) => {
    return Math.max((item.totalAccountsCount || 0) - (item.bannedAccountsCount || 0), 0);
  };

  const openAccountsByRegistrationIp = useCallback((ip?: string) => {
    if (!ip) return;

    window.open(
      getPath(routes.userList.name, undefined, { registrationIp: ip }),
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  const columns: ColumnsType<IRegistrationIp> = [
    {
      dataIndex: 'ip',
      key: 'ip',
      title: content.ip,
      render: (ip?: string) =>
        ip ? (
          <Typography.Link onClick={() => openAccountsByRegistrationIp(ip)}>{ip}</Typography.Link>
        ) : (
          '—'
        ),
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: content.status,
      render: (status?: string) => <>{getStatusLabel(status)}</>,
    },
    {
      dataIndex: 'bannedAccountsCount',
      key: 'bannedAccountsCount',
      title: content.bannedAccountsCount,
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'bannedAccountsCount'
          ? (`${currentSortDirection}end` as SortOrder)
          : undefined,
    },
    {
      dataIndex: 'totalAccountsCount',
      key: 'totalAccountsCount',
      title: content.totalAccountsCount,
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'totalAccountsCount'
          ? (`${currentSortDirection}end` as SortOrder)
          : undefined,
    },
    {
      dataIndex: 'bannedAccountsRatio',
      key: 'bannedAccountsRatio',
      title: content.bannedAccountsRatio,
      render: (value: number) => `${Math.round((value || 0) * 100)}%`,
    },
    {
      dataIndex: 'banDate',
      key: 'banDate',
      title: content.banDate,
      render: (value?: string) => (value ? moment(value).format(DATE_FORMAT) : '—'),
    },
    {
      dataIndex: 'unbanDate',
      key: 'unbanDate',
      title: content.unbanDate,
      render: (value?: string) => (value ? moment(value).format(DATE_FORMAT) : '—'),
    },
    {
      dataIndex: 'lastRegistrationDate',
      key: 'lastRegistrationDate',
      title: content.lastRegistrationDate,
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'lastRegistrationDate'
          ? (`${currentSortDirection}end` as SortOrder)
          : undefined,
      render: (value?: string) => (value ? moment(value).format(DATE_FORMAT) : '—'),
    },
    {
      key: 'actions',
      render: (_, item) => {
        if (!isAdmin) return null;

        const isBanned = item.status === 'BAN';
        const isBanLoading = preloaders.banRegistrationIp === item.ip;
        const isUnbanLoading = preloaders.unbanRegistrationIp === item.ip;
        const isLoading = isBanLoading || isUnbanLoading;

        if (isBanned) {
          return (
            <Tooltip title={content.unban}>
              <Popconfirm
                placement='left'
                title={content.unban}
                onConfirm={() =>
                  unbanRegistrationIp({
                    ip: item.ip,
                    preloaderId: item.ip,
                  })
                }
              >
                <Button
                  loading={isLoading}
                  disabled={isLoading}
                  icon={<UnlockOutlined />}
                  shape='circle'
                />
              </Popconfirm>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={content.ban}>
            <Popconfirm
              placement='left'
              overlayStyle={{ width: 300 }}
              title={content.banConfirm.replace('{count}', `${getAffectedAccountsCount(item)}`)}
              onConfirm={() =>
                banRegistrationIp({
                  ip: item.ip,
                  preloaderId: item.ip,
                })
              }
            >
              <Button
                loading={isLoading}
                disabled={isLoading}
                icon={<LockOutlined />}
                shape='circle'
              />
            </Popconfirm>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Table
      rowKey='ip'
      dataSource={list}
      columns={columns}
      loading={!!preloaders.getList}
      onChange={onChange}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions,
        position: ['topRight', 'bottomRight'],
      }}
    />
  );
};

export default RegistrationIpsTable;
